/**
 * Пузырь сообщения (C-07, C-15…C-20).
 *
 * ПС §11: reply с превью исходного, реакции, отметка «изменено»,
 * нейтральная отметка на месте удалённого «для всех», понятный статус
 * отправки. ПС §20.9: ошибка вложения не превращается в «успешное» сообщение.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { MediaVariant } from '@shared/api/types'
import type { LocalMessage } from '@shared/db'
import { useMediaUrl } from '@features/media/use-media-url'
import { splitLinks, type ParsedLink } from '@shared/lib/links'
import { Icon, Spinner, cx } from '@shared/ui'

import { emojiOnlyCount } from '../../lib/emoji-only'
import { formatDuration, formatMessageTime } from '../../lib/format'
import { MessageReceipt } from './MessageReceipt'
import { mediaLabel } from './message-text'
import styles from './message.module.css'

/** JS-анимации уважают системную настройку так же, как CSS-токены. */
function reducedMotion(): boolean {
  return globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
}

interface MessageBubbleProps {
  message: LocalMessage
  mine: boolean
  /** Продолжение серии одного автора — верхний угол у стыка скруглён меньше. */
  grouped: boolean
  /** Последнее сообщение серии — с «хвостиком», как в Telegram. */
  tail: boolean
  highlighted?: boolean
  counterpartName: string | null
  myUserId: string
  onOpenMedia: (mediaId: string) => void
  onContextMenu: (message: LocalMessage, anchor: DOMRect) => void
  onRetry: (message: LocalMessage) => void
  onDiscard: (message: LocalMessage) => void
  onScrollToReply: (seq: number) => void
  /** C-22: внешняя ссылка открывается только после предупреждения. */
  onOpenLink: (link: ParsedLink) => void
  /** Нажатие на реакцию под пузырём: своя снимается, чужая — ставится такая же (как в Telegram). */
  onReact: (message: LocalMessage, reaction: string | null) => void
  /** Свайп вправо по сообщению — ответ на него. */
  onReply: (message: LocalMessage) => void
  /** Реакция по двойному касанию (первая из набора); `null` — набора нет. */
  quickReaction: string | null
  /** Сообщение только что появилось: входящее — снизу, своё — из строки ввода (один раз). */
  entering?: 'in' | 'out' | false
  onEntered?: () => void
  /** Пост канала: открыть обсуждение. Не задан — канал без комментариев или это переписка. */
  onOpenComments?: ((message: LocalMessage) => void) | undefined
  /**
   * Автор чужого сообщения в группе (изменение v1.10). В переписке и в канале не задан:
   * там собеседник один, а посты идут от самого канала — как в Telegram.
   */
  sender?: { name: string | null; color: string } | undefined
  /** Аватар автора — под последним пузырём серии, слева от него (как в Telegram). */
  senderAvatar?: React.ReactNode | undefined
  /** Имя в цитате ответа: в группе это автор исходного сообщения, а не собеседник. */
  replyAuthorName?: string | null | undefined
}

/** Смещение, после которого свайп считается «ответить». */
const SWIPE_REPLY_PX = 56
/** Дальше пузырь не уезжает — жест уже засчитан. */
const SWIPE_MAX_PX = 84

export function MessageBubble({
  message,
  mine,
  grouped,
  tail,
  highlighted = false,
  counterpartName,
  myUserId,
  onOpenMedia,
  onContextMenu,
  onRetry,
  onDiscard,
  onScrollToReply,
  onOpenLink,
  onReact,
  onReply,
  quickReaction,
  onOpenComments,
  entering = false,
  onEntered,
  sender,
  senderAvatar,
  replyAuthorName,
}: MessageBubbleProps): React.ReactElement {
  const bubbleRef = useRef<HTMLDivElement>(null)
  const hintRef = useRef<HTMLSpanElement>(null)
  const swipeRef = useRef<{ x: number; y: number; dx: number; active: boolean } | null>(null)
  const failed = message.local_state === 'failed'

  /*
   * CC §5.3 правило 6: `type = system` и неизвестный тип показываются
   * нейтральным системным блоком по полю `text`; такое сообщение
   * не приписывается контрагенту и не редактируется.
   */
  if (message.sender_id === null) {
    return <div className={styles['daySeparator']}>{message.text}</div>
  }

  const openMenu = (): void => {
    const rect = bubbleRef.current?.getBoundingClientRect()
    if (rect) {
      // Модальный слой вернёт фокус на то же сообщение после закрытия.
      bubbleRef.current?.focus({ preventScroll: true })
      onContextMenu(message, rect)
    }
  }

  /*
   * Свайп вправо = «ответить» (ПС §11, жест как в Telegram). Только касание:
   * вертикальное движение отдаётся прокрутке (`touch-action: pan-y` у строки),
   * горизонтальное тянет пузырь до предела и по отпусканию засчитывает жест.
   */
  const resetSwipe = (): void => {
    const bubble = bubbleRef.current
    const hint = hintRef.current
    if (bubble) {
      bubble.style.transition = reducedMotion() ? '' : 'transform var(--duration-fast) var(--ease-out)'
      bubble.style.transform = ''
      setTimeout(() => {
        bubble.style.transition = ''
      }, 180)
    }
    if (hint) hint.style.opacity = ''
    swipeRef.current = null
  }

  const swipeHandlers = message.deleted
    ? {}
    : {
        onPointerDown: (event: React.PointerEvent) => {
          if (event.pointerType !== 'touch') return
          swipeRef.current = { x: event.clientX, y: event.clientY, dx: 0, active: false }
        },
        onPointerMove: (event: React.PointerEvent) => {
          const swipe = swipeRef.current
          if (!swipe) return
          const dx = event.clientX - swipe.x
          const dy = event.clientY - swipe.y
          if (!swipe.active) {
            if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
              swipeRef.current = null
              return
            }
            if (dx < 12) return
            swipe.active = true
          }
          swipe.dx = dx
          const shift = Math.max(0, Math.min(dx, SWIPE_MAX_PX))
          if (bubbleRef.current) bubbleRef.current.style.transform = `translateX(${shift}px)`
          if (hintRef.current) hintRef.current.style.opacity = String(Math.min(1, shift / SWIPE_REPLY_PX))
        },
        onPointerUp: () => {
          const swipe = swipeRef.current
          if (swipe?.active && swipe.dx >= SWIPE_REPLY_PX) onReply(message)
          if (swipe) resetSwipe()
        },
        onPointerCancel: resetSwipe,
      }

  // Сообщение из одних эмодзи (без цитаты и медиа) — крупно и без пузыря
  const emojiCount =
    message.deleted || message.media || message.local_media || message.reply_to
      ? 0
      : emojiOnlyCount(message.text)

  // Фото или видео без подписи: время и статус лежат поверх картинки в тёмной пилюле (как в Telegram)
  const visualMedia = !message.deleted && (message.media?.kind === 'image' || message.media?.kind === 'video')
  const metaOnMedia = visualMedia && !message.text?.trim() && !message.reply_to

  return (
    <div
      className={cx(
        styles['row'],
        mine ? styles['rowOut'] : styles['rowIn'],
        grouped && styles['rowGrouped'],
        highlighted && styles['highlighted'],
        entering === 'in' && styles['rowEnter'],
        entering === 'out' && styles['rowEnterOut'],
      )}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget) onEntered?.()
      }}
      {...swipeHandlers}
    >
      {message.deleted ? null : (
        <span ref={hintRef} className={styles['swipeHint']} aria-hidden>
          <Icon name="reply" size={18} />
        </span>
      )}

      {/*
       * Жёлоб под аватар держится у всех чужих сообщений серии, а сам аватар рисуется
       * только под последним — так пузыри одного автора стоят ровной колонкой (как в Telegram).
       */}
      {!mine && sender ? (
        <span className={styles['senderAvatar']} aria-hidden>
          {tail ? senderAvatar : null}
        </span>
      ) : null}

      <div
        ref={bubbleRef}
        role="group"
        tabIndex={message.deleted ? undefined : 0}
        aria-label={message.deleted ? undefined : `Сообщение: ${message.text || mediaLabel(message.type)}`}
        aria-haspopup={message.deleted ? undefined : 'dialog'}
        aria-keyshortcuts={message.deleted ? undefined : 'Enter Shift+F10 ContextMenu'}
        className={cx(
          styles['bubble'],
          mine ? styles['bubbleOut'] : styles['bubbleIn'],
          grouped && styles['bubbleGrouped'],
          tail ? styles['bubbleTail'] : styles['bubbleJoined'],
          message.deleted && styles['bubbleTombstone'],
          failed && styles['bubbleFailed'],
          (message.media || message.local_media) && !message.deleted && styles['bubbleMedia'],
          emojiCount > 0 && styles['bubbleEmoji'],
          emojiCount > 0 && styles[`bubbleEmoji${emojiCount}`],
        )}
        onContextMenu={(event) => {
          if (message.deleted) return
          event.preventDefault()
          openMenu()
        }}
        onKeyDown={(event) => {
          // Не перехватываем клавиши у ссылки, плеера, цитаты или реакции внутри.
          if (message.deleted || event.target !== event.currentTarget) return
          if (
            event.key === 'Enter' ||
            event.key === 'ContextMenu' ||
            (event.shiftKey && event.key === 'F10')
          ) {
            event.preventDefault()
            openMenu()
          }
        }}
        onDoubleClick={() => {
          // Двойное касание — быстрая реакция (как в Telegram); меню — долгое нажатие / правая кнопка.
          if (message.deleted || !quickReaction) return
          const already = message.reactions.some(
            (r) => r.user_id === myUserId && r.reaction === quickReaction,
          )
          onReact(message, already ? null : quickReaction)
        }}
      >
        {message.deleted ? (
          <BubbleBody>Сообщение удалено</BubbleBody>
        ) : (
          <>
            {/* Имя автора — над первым пузырём серии, своим цветом: в группе иначе не разобрать, кто пишет */}
            {!mine && sender && !grouped ? (
              <span className={styles['senderName']} style={{ color: sender.color }}>
                {sender.name ?? 'Участник'}
              </span>
            ) : null}

            {message.reply_to ? (
              <button
                type="button"
                className={styles['replyQuote']}
                onClick={() => message.reply_to && onScrollToReply(message.reply_to.seq)}
              >
                <span className={styles['replyAuthor']}>
                  {message.reply_to.sender_id === myUserId
                    ? 'Вы'
                    : (replyAuthorName ?? counterpartName ?? 'Собеседник')}
                </span>
                <span className={styles['replyText']}>
                  {message.reply_to.deleted
                    ? 'Сообщение удалено'
                    : (message.reply_to.text_preview ?? mediaLabel(message.reply_to.type))}
                </span>
              </button>
            ) : null}

            {message.forwarded ? (
              <span className={styles['forwardLabel']}>
                <Icon name="forward" size={14} />
                Переслано
              </span>
            ) : null}

            <MessageContent message={message} onOpenMedia={onOpenMedia} onOpenLink={onOpenLink} />
          </>
        )}

        <span
          className={cx(
            styles['meta'],
            mine ? styles['metaOut'] : styles['metaIn'],
            metaOnMedia && styles['metaOnMedia'],
          )}
        >
          {message.edited_at && !message.deleted ? (
            <span className={styles['edited']} title="Сообщение изменено">
              изменено
            </span>
          ) : null}
          {/* Изменение v1.8: у поста канала вместо галочек — счётчик просмотров, как в Telegram */}
          {typeof message.views === 'number' ? (
            <span className={styles['views']} title="Просмотры">
              <Icon name="eye" size={13} />
              {message.views}
            </span>
          ) : null}
          <time dateTime={message.created_at}>{formatMessageTime(message.created_at)}</time>
          {mine && typeof message.views !== 'number' ? (
            <MessageReceipt state={message.local_state} onBubble />
          ) : null}
        </span>

        {onOpenComments && !message.deleted ? (
          <button type="button" className={styles['commentsButton']} onClick={() => onOpenComments(message)}>
            <Icon name="comment" size={14} />
            {message.comments_count
              ? `${message.comments_count} ${commentsWord(message.comments_count)}`
              : 'Комментировать'}
          </button>
        ) : null}

        {message.reactions.length ? (
          <div className={styles['reactions']}>
            {groupReactions(message.reactions, myUserId).map(({ reaction, count, mine: isMine }) => (
              <button
                key={reaction}
                type="button"
                className={cx(styles['reaction'], isMine && styles['reactionMine'])}
                aria-pressed={isMine}
                aria-label={`Реакция ${reaction}${count > 1 ? `, ${count}` : ''}`}
                onClick={() => onReact(message, isMine ? null : reaction)}
              >
                {reaction}
                {count > 1 ? <span>{count}</span> : null}
              </button>
            ))}
          </div>
        ) : null}

        {failed ? (
          <div className={styles['failedRow']} role="alert">
            <Icon name="alert" size={14} />
            <span>{failureText(message)}</span>
            <button type="button" className={styles['failedAction']} onClick={() => onRetry(message)}>
              Повторить
            </button>
            <button type="button" className={styles['failedAction']} onClick={() => onDiscard(message)}>
              Удалить
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function BubbleBody({ children }: { children: React.ReactNode }) {
  return <span>{children}</span>
}

/* ===================================================================== */
/* Содержимое                                                             */
/* ===================================================================== */

function MessageContent({
  message,
  onOpenMedia,
  onOpenLink,
}: {
  message: LocalMessage
  onOpenMedia: (mediaId: string) => void
  onOpenLink: (link: ParsedLink) => void
}) {
  const media = message.media
  const draft = message.local_media

  // Вложение ещё грузится — показываем прогресс, а не «успешное» сообщение.
  if (!media && draft) {
    return (
      <>
        <PendingMedia draft={draft} />
        {message.text ? <BubbleBody>{linkify(message.text, onOpenLink)}</BubbleBody> : null}
      </>
    )
  }

  if (media?.kind === 'image') {
    return (
      <>
        <MessageImage media={media} onOpen={() => onOpenMedia(media.id)} />
        {message.text ? <BubbleBody>{linkify(message.text, onOpenLink)}</BubbleBody> : null}
      </>
    )
  }

  if (media?.kind === 'audio') {
    return <VoiceMessage key={media.id} message={message} />
  }

  if (media?.kind === 'video') {
    return (
      <>
        <MessageVideo media={media} onOpen={() => onOpenMedia(media.id)} />
        {message.text ? <BubbleBody>{linkify(message.text, onOpenLink)}</BubbleBody> : null}
      </>
    )
  }

  return <BubbleBody>{linkify(message.text ?? '', onOpenLink)}</BubbleBody>
}

/** URL композера отзывается после отправки; пузырь владеет своим превью. */
function PendingMedia({ draft }: { draft: NonNullable<LocalMessage['local_media']> }) {
  const [preview, setPreview] = useState<{ blob: Blob; url: string } | null>(null)
  const blob = draft.preview_blob
  useEffect(() => {
    if (!blob) return
    const url = URL.createObjectURL(blob)
    setPreview({ blob, url })
    return () => URL.revokeObjectURL(url)
  }, [blob])
  const url = blob ? (preview?.blob === blob ? preview.url : null) : draft.preview_url
  const visual = draft.content_type?.startsWith('image/')
  return (
    <div className={styles['mediaProgress']}>
      {url && visual ? <img className={styles['mediaImage']} src={url} alt="" /> : <Spinner size={24} />}
    </div>
  )
}

/**
 * Фотография в сообщении.
 *
 * Подписанный URL обновляется отдельным запросом по `media_id` (CC §8.8 п. 2):
 * перезапрашивать историю ради подписи ЗАПРЕЩЕНО. Если подпись всё же
 * протухла — один повтор, затем плейсхолдер вместо бесконечных попыток
 * (CC §11.2 п. 4).
 */
function MessageImage({ media, onOpen }: { media: NonNullable<LocalMessage['media']>; onOpen: () => void }) {
  const { url, failed, onError } = useMediaUrl(media, 'full')

  if (failed || !url) {
    return (
      <div className={styles['mediaPlaceholder']}>
        <Icon name="image" size={22} />
        <span>Изображение недоступно</span>
      </div>
    )
  }

  return (
    <img
      className={styles['mediaImage']}
      src={url}
      alt="Фотография в сообщении"
      loading="lazy"
      decoding="async"
      onError={onError}
      onClick={onOpen}
    />
  )
}

/**
 * Видео в ленте — кадр-постер с кнопкой воспроизведения; само видео играет в
 * просмотрщике C-09 (§10.5 п. 1: запуск только по действию пользователя,
 * `preload="none"`). Встроенные нативные контролы в маленьком пузыре
 * промахивались пальцем и выглядели чужеродно в обеих темах.
 */
function MessageVideo({ media, onOpen }: { media: NonNullable<LocalMessage['media']>; onOpen: () => void }) {
  const poster = media.variants.find((v) => v.name === 'poster')
  const { url, failed, onError } = useMediaUrl(media, 'poster')
  const duration = media.variants.find((v) => typeof v.duration_ms === 'number')?.duration_ms ?? null

  return (
    <button type="button" className={styles['videoPoster']} onClick={onOpen} aria-label="Открыть видео">
      {poster && url && !failed ? (
        <img
          className={styles['mediaImage']}
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={onError}
        />
      ) : (
        <span className={styles['videoPlaceholder']} aria-hidden />
      )}
      <span className={styles['videoPlay']} aria-hidden>
        <Icon name="play" size={22} />
      </span>
      {duration !== null ? <span className={styles['videoDuration']}>{formatDuration(duration)}</span> : null}
    </button>
  )
}

/* ===================================================================== */
/* Голосовое сообщение (C-26)                                             */
/* ===================================================================== */

/** IOS §7.4 п. 6: одновременно воспроизводится не более одного голосового. */
let activeVoice: HTMLAudioElement | null = null

function voiceVariantFamily(variant: MediaVariant): 'opus' | 'mp4' | 'other' {
  const name = variant.name.toLowerCase()
  const contentType = variant.content_type.toLowerCase()
  const baseType = contentType.split(';', 1)[0]?.trim()
  if (
    name === 'opus' ||
    contentType.includes('opus') ||
    baseType === 'audio/ogg' ||
    baseType === 'audio/webm'
  ) {
    return 'opus'
  }
  if (name === 'aac' || name === 'm4a' || baseType === 'audio/mp4' || baseType === 'audio/aac') {
    return 'mp4'
  }
  return 'other'
}

/**
 * Базовый MIME говорит только о контейнере. Проверяем кодек
 * явно: Opus для Ogg/WebM и гарантированный backend AAC-LC для MP4.
 */
function voiceCapabilityType(variant: MediaVariant): string {
  const contentType = variant.content_type.trim()
  const baseType = contentType.split(';', 1)[0]?.trim().toLowerCase()
  if (
    voiceVariantFamily(variant) === 'opus' &&
    (baseType === 'audio/ogg' || baseType === 'audio/webm') &&
    !/;\s*codecs\s*=/i.test(contentType)
  ) {
    return `${baseType}; codecs="opus"`
  }
  if (
    voiceVariantFamily(variant) === 'mp4' &&
    baseType === 'audio/mp4' &&
    !/;\s*codecs\s*=/i.test(contentType)
  ) {
    return `${baseType}; codecs="mp4a.40.2"`
  }
  return contentType
}

function voiceCapabilityConfidence(support: CanPlayTypeResult): number {
  if (support === 'probably') return 2
  if (support === 'maybe') return 1
  return 0
}

function isPlaybackAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
}

interface VoicePlaybackAttempt {
  id: number
  element: HTMLAudioElement
  sourceIdentity: string
  variantName: string | null
  url: string | null
}

/**
 * Сначала выбираем вариант с наиболее уверенной поддержкой браузера.
 * При одинаковой уверенности основной вариант остаётся Opus. Пустая
 * строка и старое нестандартное `no` означают «не поддерживается».
 */
function createVoiceVariantSelector(
  failedVariantNames: ReadonlySet<string> = new Set(),
): (variants: readonly MediaVariant[]) => MediaVariant | undefined {
  if (typeof document === 'undefined') return (variants) => variants[0]
  const audio = document.createElement('audio')
  return (variants) => {
    const supported = variants
      .map((variant) => ({
        variant,
        confidence: voiceCapabilityConfidence(audio.canPlayType(voiceCapabilityType(variant))),
      }))
      .filter(({ confidence }) => confidence > 0)
    const alternatives = supported.filter(({ variant }) => !failedVariantNames.has(variant.name))
    const candidates = alternatives.length > 0 ? alternatives : supported
    let selected: MediaVariant | undefined
    let selectedConfidence = 0
    for (const { variant, confidence } of candidates) {
      const preferOpusOnTie =
        confidence === selectedConfidence &&
        voiceVariantFamily(variant) === 'opus' &&
        selected !== undefined &&
        voiceVariantFamily(selected) !== 'opus'
      if (confidence > selectedConfidence || preferOpusOnTie) {
        selected = variant
        selectedConfidence = confidence
      }
    }
    return selected
  }
}

function VoiceMessage({ message }: { message: LocalMessage }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [failed, setFailed] = useState(false)
  const [failedVariantNames, setFailedVariantNames] = useState<readonly string[]>([])
  const playbackAttemptSequenceRef = useRef(0)
  const playbackAttemptRef = useRef<VoicePlaybackAttempt | null>(null)
  const settledPlaybackAttemptIdRef = useRef<number | null>(null)
  const selectVariant = useMemo(
    () => createVoiceVariantSelector(new Set(failedVariantNames)),
    [failedVariantNames],
  )
  const initialDuration = message.media?.variants.find(
    (variant) => typeof variant.duration_ms === 'number',
  )?.duration_ms
  const [duration, setDuration] = useState(initialDuration ?? 0)
  const {
    url,
    variantName,
    failed: unavailable,
    refreshing,
    onError,
    retry,
  } = useMediaUrl(message.media, selectVariant)
  const sourceIdentity = `${variantName ?? ''}\n${url ?? ''}`
  const currentSourceIdentityRef = useRef(sourceIdentity)

  useLayoutEffect(() => {
    currentSourceIdentityRef.current = sourceIdentity
  }, [sourceIdentity])

  useEffect(() => {
    const audio = audioRef.current
    return () => {
      if (activeVoice === audio) activeVoice = null
      audio?.pause()
    }
  }, [sourceIdentity])

  // Форма волны детерминирована по id — не «прыгает» между рендерами.
  const bars = waveform(message.id)
  const position = Math.round(progress * duration)

  /** Нативный range даёт стрелки, Home/End и доступное значение без второй панели. */
  const seek = (milliseconds: number): void => {
    const audio = audioRef.current
    if (!audio || !duration) return
    audio.currentTime = milliseconds / 1000
    setProgress(milliseconds / duration)
  }

  const beginPlaybackAttempt = (
    element: HTMLAudioElement,
    attemptedSourceIdentity: string,
    attemptedVariantName: string | null,
    attemptedUrl: string | null,
  ): VoicePlaybackAttempt => {
    const attempt = {
      id: playbackAttemptSequenceRef.current + 1,
      element,
      sourceIdentity: attemptedSourceIdentity,
      variantName: attemptedVariantName,
      url: attemptedUrl,
    }
    playbackAttemptSequenceRef.current = attempt.id
    playbackAttemptRef.current = attempt
    return attempt
  }

  const markPlaybackFailed = (attempt: VoicePlaybackAttempt): void => {
    // Promise старого keyed <audio> может завершиться после выбора нового источника.
    if (attempt.element !== audioRef.current || attempt.sourceIdentity !== currentSourceIdentityRef.current)
      return
    // play() rejection и media error обычно описывают одну ошибку источника.
    if (settledPlaybackAttemptIdRef.current === attempt.id) return
    settledPlaybackAttemptIdRef.current = attempt.id
    setFailed(true)
    setPlaying(false)
    if (attempt.variantName) {
      const failedVariantName = attempt.variantName
      setFailedVariantNames((current) =>
        current.includes(failedVariantName) ? current : [...current, failedVariantName],
      )
    }
    onError()
  }

  const play = (audio: HTMLAudioElement): void => {
    if (activeVoice && activeVoice !== audio) activeVoice.pause()
    activeVoice = audio
    setFailed(false)
    const attempt = beginPlaybackAttempt(audio, sourceIdentity, variantName, url)
    // Воспроизведение — только по действию пользователя (CC §13.2 п. 7).
    audio.play().catch((error: unknown) => {
      if (isPlaybackAbort(error)) {
        // pause()/load() прерывает pending play(), но не означает ошибку формата.
        settledPlaybackAttemptIdRef.current = attempt.id
        return
      }
      markPlaybackFailed(attempt)
    })
  }

  const toggle = (): void => {
    const audio = audioRef.current
    if (!audio) return
    if (unavailable) {
      void retry().then((next) => {
        const refreshedAudio = audioRef.current
        if (!next || !refreshedAudio) return
        refreshedAudio.src = next
        const refreshedVariant = refreshedAudio.dataset.voiceVariant ?? variantName ?? null
        beginPlaybackAttempt(refreshedAudio, `${refreshedVariant ?? ''}\n${next}`, refreshedVariant, next)
        refreshedAudio.load()
        // Safari теряет user activation после await. Эта попытка только
        // обновляет источник; следующее нажатие синхронно запускает play().
        setFailed(false)
      })
      return
    }
    if (playing) audio.pause()
    else {
      // После error общий hook уже заменил подпись; load использует свежий src.
      if (failed) audio.load()
      play(audio)
    }
  }

  return (
    <div className={styles['voice']} onDoubleClick={(event) => event.stopPropagation()}>
      <button
        type="button"
        className={styles['voicePlay']}
        onClick={toggle}
        disabled={refreshing || (!url && !unavailable)}
        aria-label={
          refreshing
            ? 'Загрузка голосового'
            : failed || unavailable
              ? 'Повторить'
              : playing
                ? 'Пауза'
                : 'Слушать'
        }
      >
        {refreshing ? (
          <Spinner size={18} />
        ) : (
          <Icon name={failed || unavailable ? 'refresh' : playing ? 'pause' : 'play'} size={18} />
        )}
      </button>

      {failed || unavailable ? (
        <span className={styles['voiceError']} role="alert">
          {unavailable ? 'Голосовое недоступно' : 'Не удалось воспроизвести'}
        </span>
      ) : (
        <div className={styles['voiceWave']}>
          <div className={styles['voiceBars']} aria-hidden>
            {bars.map((height, index) => (
              <span
                key={index}
                className={cx(
                  styles['voiceBar'],
                  index / bars.length <= progress && styles['voiceBarPlayed'],
                )}
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
          <input
            className={styles['voiceSeek']}
            type="range"
            min={0}
            max={duration}
            step={1}
            value={position}
            disabled={!duration || refreshing}
            aria-label="Позиция голосового сообщения"
            aria-valuetext={`${formatDuration(position)} из ${formatDuration(duration)}`}
            onChange={(event) => seek(Number(event.currentTarget.value))}
            onKeyDown={(event) => {
              const next =
                event.key === 'Home'
                  ? 0
                  : event.key === 'End'
                    ? duration
                    : event.key === 'ArrowRight' || event.key === 'ArrowUp'
                      ? position + 5000
                      : event.key === 'ArrowLeft' || event.key === 'ArrowDown'
                        ? position - 5000
                        : null
              if (next === null) return
              event.preventDefault()
              seek(Math.min(duration, Math.max(0, next)))
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={() => {
              // Касание волны, как раньше, начинает проигрывание. Клавиатура только перематывает.
              if (!playing && audioRef.current) play(audioRef.current)
            }}
          />
        </div>
      )}

      <span className={styles['voiceTime']}>
        {playing || progress > 0 ? formatDuration(position) : formatDuration(duration)}
      </span>

      <audio
        key={sourceIdentity}
        ref={audioRef}
        src={url ?? undefined}
        data-voice-variant={variantName ?? undefined}
        preload="none"
        onLoadedMetadata={(event) => {
          const seconds = event.currentTarget.duration
          if (Number.isFinite(seconds) && seconds > 0) setDuration(Math.round(seconds * 1000))
        }}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(event) => {
          const el = event.currentTarget
          if (Number.isFinite(el.duration) && el.duration > 0) setProgress(el.currentTime / el.duration)
        }}
        onEnded={() => {
          setPlaying(false)
          setProgress(0)
        }}
        onError={(event) => {
          // Событие старого DOM-поколения не относится к уже выбранному источнику.
          if (event.currentTarget !== audioRef.current || currentSourceIdentityRef.current !== sourceIdentity)
            return
          const previousAttempt = playbackAttemptRef.current
          const attempt =
            previousAttempt &&
            previousAttempt.element === event.currentTarget &&
            previousAttempt.sourceIdentity === sourceIdentity &&
            previousAttempt.variantName === variantName &&
            previousAttempt.url === url
              ? previousAttempt
              : beginPlaybackAttempt(event.currentTarget, sourceIdentity, variantName, url)
          markPlaybackFailed(attempt)
        }}
      />
    </div>
  )
}

function waveform(seed: string): number[] {
  let state = 0
  for (let i = 0; i < seed.length; i += 1) state = (state * 31 + seed.charCodeAt(i)) >>> 0

  return Array.from({ length: 28 }, () => {
    state = (state * 1103515245 + 12345) >>> 0
    return 25 + ((state >>> 16) % 70)
  })
}

/* ===================================================================== */
/* Вспомогательное                                                        */
/* ===================================================================== */

interface GroupedReaction {
  reaction: string
  count: number
  mine: boolean
}

function groupReactions(
  reactions: readonly { user_id: string; reaction: string }[],
  myUserId: string,
): GroupedReaction[] {
  const map = new Map<string, GroupedReaction>()

  for (const item of reactions) {
    const mine = item.user_id === myUserId
    const existing = map.get(item.reaction)
    if (existing) {
      existing.count += 1
      existing.mine = existing.mine || mine
    } else map.set(item.reaction, { reaction: item.reaction, count: 1, mine })
  }

  return [...map.values()]
}

/**
 * ПС §11: внешние ссылки визуально распознаются как ссылки, но открываются
 * только после предупреждения C-22 (CC §12.5 п. 2) — поэтому нажатие
 * перехватывается, а не отдаётся браузеру.
 *
 * Текст остаётся текстовыми узлами React: разметка из сообщения НЕ
 * интерпретируется (CC §12.5 п. 1).
 */
function linkify(text: string, onOpenLink: (link: ParsedLink) => void): React.ReactNode {
  const parts = splitLinks(text)
  if (parts.length === 1 && parts[0]?.kind === 'text') return text

  return parts.map((part, index) =>
    part.kind === 'text' ? (
      part.value
    ) : (
      <a
        key={index}
        href={part.link.href}
        target="_blank"
        rel="noopener noreferrer nofollow"
        // Подозрительный адрес помечается до нажатия, а не только в диалоге.
        className={part.link.risk === 'suspicious' ? styles['linkSuspicious'] : undefined}
        title={part.link.host}
        onClick={(event) => {
          event.preventDefault()
          onOpenLink(part.link)
        }}
      >
        {part.link.raw}
      </a>
    ),
  )
}

function failureText(message: LocalMessage): string {
  switch (message.error_code) {
    case 'media_rejected':
      return 'Вложение не прошло проверку'
    case 'conversation_not_sendable':
      return 'В этот диалог больше нельзя писать'
    // C-23: ссылка отклонена сервером (домен запрещён или ранний диалог)
    case 'link_not_allowed':
      return 'Ссылки пока недоступны в новом диалоге'
    case 'client_message_id_conflict':
      return 'Сообщение уже отправлялось'
    case 'payload_too_large':
      return 'Сообщение слишком длинное'
    default:
      return 'Не отправлено'
  }
}

/** «1 комментарий» / «2 комментария» / «5 комментариев» (изменение v1.8). */
function commentsWord(count: number): string {
  const mod100 = count % 100
  const mod10 = count % 10
  if (mod100 >= 11 && mod100 <= 14) return 'комментариев'
  if (mod10 === 1) return 'комментарий'
  if (mod10 >= 2 && mod10 <= 4) return 'комментария'
  return 'комментариев'
}
